/**
 * Browser Notification Service
 * Handles browser notifications using the Notifications API
 */

export interface NotificationOptions {
  title: string;
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  requireInteraction?: boolean;
}

/**
 * Request permission for browser notifications
 * Returns the current permission level
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('Browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission;
  }

  return Notification.permission;
}

/**
 * Check if notifications are supported and permitted
 */
export function canShowNotifications(): boolean {
  return 'Notification' in window && Notification.permission === 'granted';
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) {
    return 'denied';
  }
  return Notification.permission;
}

/**
 * Show a browser notification
 */
export function showNotification(options: NotificationOptions): Notification | null {
  if (!canShowNotifications()) {
    return null;
  }

  try {
    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon || '/favicon.ico',
      badge: options.badge,
      tag: options.tag,
      requireInteraction: options.requireInteraction || false,
    });

    // Store data for click handler
    if (options.data) {
      (notification as any).data = options.data;
    }

    // Auto-close notification after 5 seconds if not requiring interaction
    if (!options.requireInteraction) {
      setTimeout(() => {
        notification.close();
      }, 5000);
    }

    return notification;
  } catch (error) {
    console.error('Failed to show notification:', error);
    return null;
  }
}

/**
 * Show notification for new click/lead
 */
export function showNewClickNotification(data: {
  user?: string;
  country?: string;
  city?: string;
  platform?: string;
}): Notification | null {
  const location = [data.city, data.country].filter(Boolean).join(', ') || 'Unknown';
  const platform = data.platform ? ` from ${data.platform}` : '';

  return showNotification({
    title: '🔔 New Click',
    body: `User ${data.user}${platform} - ${location}`,
    icon: '/favicon.ico',
    tag: 'new-click',
    data: { type: 'click', ...data },
  });
}

/**
 * Show notification for new conversion/lead
 */
export function showNewLeadNotification(data: {
  user?: string;
  amount?: number;
  country?: string;
  platform?: string;
}): Notification | null {
  const amount = data.amount ? ` $${data.amount.toFixed(2)}` : '';
  const platform = data.platform ? ` from ${data.platform}` : '';

  return showNotification({
    title: '💰 New Lead',
    body: `${data.user}${platform}${amount}`,
    icon: '/favicon.ico',
    tag: 'new-lead',
    data: { type: 'lead', ...data },
  });
}
